import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { updateKycWorkflowKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { UpdateKycWorkflowSettingDto } from '@dealstack/shared/lib/generated';
import { certificationToggleFollowing } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/KycSettings.utils.ts';
import { TsaKycSettings } from './types.ts';

export const useInvestmentWorkflowKycSettings = (
	kycWorkflowId: string | undefined,
	mipId: string
) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const { data: kycWorkflowSettings, isLoading: isLoadingSettings } = useKycWorkflowKycSetting(
		kycWorkflowId ?? ''
	);

	const updateMutation = useMutation({
		mutationFn: (data: UpdateKycWorkflowSettingDto) =>
			updateKycWorkflowKycSetting(kycWorkflowId!, data),
		onSuccess: async (_response, variables) => {
			queryClient.setQueryData(
				[API_PATHS.KYC_SETTING_KYCWORKFLOW_KYCWORKFLOWID_GET, kycWorkflowId],
				(oldData: { payload: typeof kycWorkflowSettings } | undefined) => {
					if (!oldData?.payload) {
						return oldData;
					}
					return {
						...oldData,
						payload: { ...oldData.payload, ...variables },
					};
				}
			);
			await queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(
						API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_LIST_POST,
						{ mipId }
					),
				],
			});
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_KYC_INVESTORS_INVESTORID_GET],
			});
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_KYC_INVESTORS_GET],
			});
			await queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_KYC_WORKFLOWS_GET],
			});
			showToastV2({ title: tMipManage('kyc_settings.requirements.updated_success') });
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('kyc_settings.requirements.updated_error'),
			});
		},
	});

	const settings = useMemo<TsaKycSettings>(
		() => ({
			kycRequired: {
				checked: kycWorkflowSettings?.isKycRequired ?? false,
				disabled: false,
			},
			proofOfIdentityCertificationRequired: {
				checked: kycWorkflowSettings?.isProofOfIdentityCertificationRequired ?? false,
				disabled: false,
			},
			proofOfAddressCertificationRequired: {
				checked: kycWorkflowSettings?.isProofOfAddressCertificationRequired ?? false,
				disabled: false,
			},
			proofOfInvestmentVehicleCertificationRequired: {
				checked:
					kycWorkflowSettings?.isProofOfInvestmentVehicleCertificationRequired ?? false,
				disabled: false,
			},
			proofOfIdentityRequired: {
				checked: kycWorkflowSettings?.isProofOfIdentityRequired ?? false,
				disabled: false,
			},
			proofOfAddressRequired: {
				checked: kycWorkflowSettings?.isProofOfAddressRequired ?? false,
				disabled: false,
			},
			proofOfInvestmentVehicleRequired: {
				checked: kycWorkflowSettings?.isProofOfInvestmentVehicleRequired ?? false,
				disabled: false,
			},
			beneficialOwnershipRequired: {
				checked: kycWorkflowSettings?.isBeneficialOwnershipRequired ?? true,
				disabled: false,
			},
			sourceOfFundsRequired: {
				checked: kycWorkflowSettings?.isSourceOfFundsRequired ?? true,
				disabled: false,
			},
			pepRequired: {
				checked: kycWorkflowSettings?.isPepRequired ?? true,
				disabled: false,
			},
		}),
		[kycWorkflowSettings]
	);

	const toggleSetting = (id: keyof TsaKycSettings) => {
		if (!kycWorkflowId || !settings || updateMutation.isPending) {
			return;
		}

		const newValue = !settings[id].checked;

		const fieldMap: Record<keyof TsaKycSettings, keyof UpdateKycWorkflowSettingDto> = {
			kycRequired: 'isKycRequired',
			proofOfIdentityRequired: 'isProofOfIdentityRequired',
			proofOfAddressRequired: 'isProofOfAddressRequired',
			proofOfInvestmentVehicleRequired: 'isProofOfInvestmentVehicleRequired',
			proofOfIdentityCertificationRequired: 'isProofOfIdentityCertificationRequired',
			proofOfAddressCertificationRequired: 'isProofOfAddressCertificationRequired',
			proofOfInvestmentVehicleCertificationRequired:
				'isProofOfInvestmentVehicleCertificationRequired',
			beneficialOwnershipRequired: 'isBeneficialOwnershipRequired',
			sourceOfFundsRequired: 'isSourceOfFundsRequired',
			pepRequired: 'isPepRequired',
		};

		const certification = certificationToggleFollowing(id, newValue);

		updateMutation.mutate({
			[fieldMap[id]]: newValue,
			...(certification ? { [fieldMap[certification.id]]: certification.value } : {}),
		});
	};

	return {
		settings,
		toggleSetting,
		isLoading: isLoadingSettings,
	};
};
